import { useSyncExternalStore } from 'react';

/**
 * What became of the last quote (or quotes) sent from a list row, outside
 * any one row.
 *
 * `SendQuoteButton` is rendered per row, and sending a quote is exactly the
 * kind of action that can make its own row disappear before its result is
 * read: the row's status moves to `sent`, the list refetches to stay
 * accurate, and a filtered view (or a sort that just moved the row off the
 * page) can unmount the button mid-message. State that lived on the button
 * would go with it — the same reason PDF downloads publish here instead of
 * keeping their status local (`use-pdf-download.ts`) — so the outcome is
 * published to this page-level store instead, read by `SendOutcomeIndicator`
 * wherever the app is mounted, and outlives the row that produced it.
 *
 * Kept as a queue rather than one slot: staff can start sends from two
 * different rows before either finishes, and a failure from one quote is not
 * something a success from another gets to silently overwrite. Each outcome
 * is shown until it is dismissed — by hand, or by the session or location it
 * belonged to going away — before the next one takes its place.
 *
 * Also stamped with an epoch. A mutation already in flight when the session
 * or location changes finishes on its own schedule regardless — React Query
 * calls a mutation's `onSuccess`/`onError` even after the component that
 * started it, and the row it lived on, are long gone — so without this a
 * result addressed to the *previous* tenant or branch would land after
 * `resetSendOutcome` already ran and reappear in the new one. A caller reads
 * `sendOutcomeEpoch()` before starting work and passes it back in; a publish
 * whose epoch no longer matches belongs to a boundary that has since closed,
 * and is dropped rather than shown.
 */

export interface SendOutcome {
  tone: 'success' | 'warning' | 'error';
  text: string;
}

let queue: readonly SendOutcome[] = [];
let epoch = 0;
const listeners = new Set<() => void>();

function publish(next: readonly SendOutcome[]): void {
  queue = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): readonly SendOutcome[] {
  return queue;
}

/** The outcome due to be read next, or null while none is waiting. */
export function useSendOutcome(): SendOutcome | null {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  return current[0] ?? null;
}

/** How many more are waiting behind the one on screen. */
export function useSendOutcomeQueueSize(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot).length;
}

/**
 * The store's current epoch. Read this before starting a send and hand it
 * back to `publishSendOutcome` when the send settles — see this module's own
 * comment for why a result cannot simply be published unconditionally.
 */
export function sendOutcomeEpoch(): number {
  return epoch;
}

/**
 * Enqueues a new outcome; it is shown once every earlier one has been
 * dismissed. `forEpoch`, when given, must still match `sendOutcomeEpoch()` at
 * the moment of the call — a session or location boundary since the caller
 * started bumps the epoch, and a late result addressed to the closed one is
 * silently dropped rather than shown to whoever is looking at the screen now.
 */
export function publishSendOutcome(outcome: SendOutcome, forEpoch?: number): void {
  if (forEpoch !== undefined && forEpoch !== epoch) return;
  publish([...queue, outcome]);
}

/** Drops the one currently on screen, revealing the next if there is one. */
export function dismissSendOutcome(): void {
  if (queue.length === 0) return;
  publish(queue.slice(1));
}

/**
 * Drops everything queued and closes out the current epoch — a session or
 * location boundary, not a single read. Any send still in flight from before
 * this call publishes into the new epoch and is discarded on arrival.
 */
export function resetSendOutcome(): void {
  epoch += 1;
  publish([]);
}
